import { TestLogic, ILandoltContainer, IDirection, NextLandoltIs } from './test-logic';
import { GammaCorrectionLogic } from './gamma-correction-logic';
import { contrastTestData, ContrastTestObject, resetContrastData } from './contrast-test-data';
import { IContrastResultTelemetryData } from '../../result/util/typings';


export class ContrastTestLogic extends TestLogic {
    private _gammaCorrectionLogic = GammaCorrectionLogic.getInstance();
    /**
     * Reusing this as a container to pass data to the react-component; the size stays the same only
     * the opacity may vary.
     */
    private _smallestCorrectLandolt: ILandoltContainer;
    private readonly _testDataLength: number;
    private _currentIndex: number;
    private _storagePrefix: string = 'contrastTest_';

    constructor() {
        super();
        this._testDataLength = contrastTestData.length;
        this._smallestCorrectLandolt = this._landolts[0];
        this._smallestCorrectLandolt.baseSize = 468;
        this._currentIndex = 0;
    }

    type = (): string => 'ContrastTestLogic';

    current = (): ILandoltContainer => {
        const newPercentage = contrastTestData[this._currentIndex].percentage;
        this._smallestCorrectLandolt.color = this._gammaCorrectionLogic.getCorrectedColor(newPercentage);
        return this._smallestCorrectLandolt;
    };

    apply(direction: IDirection): boolean {
        ++contrastTestData[this._currentIndex].seen;
        if (this._smallestCorrectLandolt.direction.direction === direction) {
            ++contrastTestData[this._currentIndex].correctGuess;
            // User has guessed the current ring direction correctly two times.
            if (contrastTestData[this._currentIndex].correctGuess === 2) {
                contrastTestData[this._currentIndex].identified = true;
                contrastTestData[this._currentIndex].identifiedCorrectly = true;
                this._storageService.set(
                    this._storagePrefix + 'result', JSON.stringify(contrastTestData[this._currentIndex])
                );
                return this._getNextLandoltForContrastTest('lighter')
            } else {
                this._smallestCorrectLandolt.direction =
                    TestLogic._getDifferentDirection(this._smallestCorrectLandolt.direction);
                return true;
            }
        } else {
            ++contrastTestData[this._currentIndex].wrongGuess;
            // User has guessed the biggest ring wrongly. Change the ring direction and show the same size again.
            if (this._currentIndex === 0 && contrastTestData[this._currentIndex].wrongGuess === 1) {
                this._smallestCorrectLandolt.direction =
                    TestLogic._getDifferentDirection(this._smallestCorrectLandolt.direction);
                return true;
            }

            // User has already guessed the current ring wrongly and now they did it a second time.
            if (contrastTestData[this._currentIndex].wrongGuess === 2) {
                contrastTestData[this._currentIndex].identified = true;
                contrastTestData[this._currentIndex].identifiedWrongly = true;
            }
            // In case of a wrong guess we don’t show the same ring again but rather go one step bigger.
            return this._getNextLandoltForContrastTest('darker');
        }
    }

    resetState(): void {
        this._storageService.del([`${this._storagePrefix}result`]);
        this._globalLogic.removeTestFromFinishedList('contrast-test');
        this._smallestCorrectLandolt = this._landolts[0];
        this._currentIndex = 0;
        resetContrastData();
    }

    getCurrentOpacityPercentage(): number {
        return contrastTestData[this._currentIndex].percentage;
    }

    getCurrentCorrectGuesses(): number {
        return contrastTestData[this._currentIndex].correctGuess;
    }

    getCurrentWrongGuesses(): number {
        return contrastTestData[this._currentIndex].wrongGuess;
    }

    getContrastTestObject(): ContrastTestObject {
        const buf = this._storageService.get(this._storagePrefix + 'result');
        if (buf) {
            return JSON.parse(buf) as ContrastTestObject;
        }
        return contrastTestData[this._currentIndex];
    }

    get smallestCorrectLandolt(): ILandoltContainer {
        const buf = this._storageService.get(this._storagePrefix + 'result');
        let percentage = 100;
        if (buf) {
            const c: ContrastTestObject = JSON.parse(buf);
            percentage = c.percentage;
        }
        this._smallestCorrectLandolt.color = this._gammaCorrectionLogic.getCorrectedColor(percentage);
        return this._smallestCorrectLandolt;
    }

    get smallestCorrectLandoltReducedOpacity(): ILandoltContainer {
        // TODO: This is a hack because of the interface
        return this._smallestCorrectLandolt;
    }

    getTelemetryData(): IContrastResultTelemetryData {
        if (!this.finished) {
            return { percentage: null, rating: null };
        }
        const resultObject = this.getContrastTestObject();
        return {
            percentage: resultObject.percentage,
            rating: resultObject.rating.toString()
        };
    }

    protected _getNextLandolt(_nextLandoltIs: NextLandoltIs): boolean {
        throw new Error('Method not implemented.');
    }

    private _getNextLandoltForContrastTest(nextLandoltIs: 'darker' | 'lighter' = 'lighter'): boolean {
        if (nextLandoltIs === 'lighter') {
            if (this._currentIndex === this._testDataLength - 1) {
                this._markTestAsFinished();
                return false;
            }

            // User has identified the second last ring. Show them the last one.
            if (this._currentIndex === this._testDataLength - 2) {
                --this._currentIndex;
                if (!contrastTestData[this._currentIndex].identified) {
                    return true;
                }
                this._markTestAsFinished();
                return false;
            }

            // Show two sizes smaller
            this._currentIndex += 2;
            this._smallestCorrectLandolt.direction =
                TestLogic._getDifferentDirection(this._smallestCorrectLandolt.direction);

            if (contrastTestData[this._currentIndex].identified) {
                return this._getNextLandoltForContrastTest('lighter');
            }
        } else {
            // User could not identify the biggest ring. Don‘t show any more rings.
            if (this._currentIndex === 0) {
                this._markTestAsFinished();
                return false;
            }

            // Show one size bigger
            --this._currentIndex;
            if (contrastTestData[this._currentIndex].identified) {
                if (contrastTestData[this._currentIndex].identifiedCorrectly) {
                    this._markTestAsFinished();
                    return false;
                }
                this._smallestCorrectLandolt.direction =
                    TestLogic._getDifferentDirection(this._smallestCorrectLandolt.direction);
                return this._getNextLandoltForContrastTest('darker');
            }
        }
        return true;
    }
}
